import { InvisibleButton } from "../Buttons/Buttons";
import type {
  TagClassificationConfig,
  Filters,
  ProductFiltersMainSchema,
  SupportedLanguage,
  IPresetFilter,
  IFilters,
  IPresetDataItem,
  WithPagination,
} from "../../types/types";
import type {
  ProductStatusesSchema,
  AttributeTemplateSummarySchema,
  ProductStatusType,
} from "../../types/types.PIM";
import * as React from "react";
import { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import useSWR from "swr";
import { ArrayParam, StringParam, useQueryParams } from "use-query-params";
import type { ChipType } from "../Chips/Chips";
import { endpoints } from "../../endpoints";
import {
  convertToChipArray,
  getCustomLabel,
  makeUrlWithParams,
  useStoreState,
} from "../../util/util";
import { useCookies } from "react-cookie";
import noop from "lodash/noop";
import { FilterGroup } from "./FilterGroup";
import { XIcon } from "../Icons/Icons";
import { SearchBar } from "../SearchBar/SearchBar";
import {
  convertToFiltersFormat,
  getStorageMethod,
  loadFilters,
  saveFilters,
} from "../../util/filterStateManager";
import { useHistory, useLocation } from "react-router-dom";
import { screenSize } from "../../theme";
import { PresetFilter } from "./PresetFilter";
import { useAuthContext } from "../Auth";
import type { AxiosError } from "axios";
import axios from "axios";
import { Notifications } from "../Notifications/NotificationsContext";
import { getProductStatusText, getProductStatusValue } from "../Status/Status";
import { useAlternateRequest } from "../../hooks/useAlternateRequest";
import { ENTITY_MODIFY_PERMISSIONS, EntityType } from "../../types/types.MDM";
import {
  chipsToStringArray,
  decodeNumericFilterCriterion,
  expandNumericFilterParams,
  facetOptionValueMatches,
  isIdKeyedFacetType,
} from "./filterutils";
import { useMdmEntityDisplayNames } from "../../util/mdmEntityDisplayNames";

interface IFiltersFlyBarWrapperProps {
  show: boolean;
}

interface IsearchFilterItem {
  type: string;
  label: string;
  // The facet entry's id + value type (e.g. "user"); used to apply id-keyed
  // facets by id from the search box.
  id?: string | number;
  valueType?: string;
}

const FiltersFlyBarWrapper = styled.div<IFiltersFlyBarWrapperProps>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: ${({ show }) => (show ? "100%" : "0")};
  height: 100%;
  @media ${screenSize.medium} {
    position: ${({ show }) => (show ? "sticky" : "absolute")};
    top: 1px;
    height: calc(100vh - 141px);
    background: ${({ theme }) => theme.primaryBG};
    z-index: 3;
  }
  @media ${screenSize.small} {
    width: 100%;
  }
`;

const FiltersHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) =>
      theme.resolve("color_filterSearchBar_border") ?? theme.primaryBorder};
  background: ${({ theme }) => theme.resolve("color_filterSearchBar_bg")};
  padding: ${({ theme }) =>
    ` ${theme.resolve("spacing_lg")} ${theme.resolve("spacing_xl")}`};
  position: sticky;
  top: 0;
  z-index: 3;
`;

const FiltersHeader = styled.div`
  display: flex;
  flex-direction: row;
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.resolve("font_weight_medium")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
  justify-content: space-between;
  align-items: center;
  button {
    display: flex;
    padding: 5px;
    background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  }
  svg {
    transform: rotate(90deg);
    &:hover {
      transform: scale(1.2);
    }
  }
`;

const SearchBarWrapper = styled.div`
  position: relative;
  input {
    border-radius: ${({ theme }) => theme.resolve("radius_xs")};
    background: ${({ theme }) => theme.primaryBG};
  }
  svg {
    left: 7px;
    path {
      fill: #000;
    }
  }
`;

const FiltersWrapper = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  }
`;

const EmptyFiltersPlaceholder = styled.div`
  width: 100%;
  height: 30px;
  margin: 0 0 35px;
`;

const SearchResultsWrapper = styled.div`
  position: absolute;
  top: 40px;
  left: 0px;
  max-height: 300px;
  overflow: auto;
  width: 100%;
  border: ${({ theme }) => theme.resolve("border_md")} solid ${({ theme }) =>
  theme.primaryBorder};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  box-shadow: 2px 2px 2px #00000020;
  background: ${({ theme }) => theme.primaryBG};
  font-size: ${({ theme }) => theme.fontSizes.small};
  z-index: 2;
  }

  ul {
    padding: 0;
    margin: 0;
    li {
      padding: 10px;
      list-style: none;
      font-size: ${({ theme }) => theme.fontSizes.small};
      transition: all 200ms ease;
      &:hover {
        background: ${({ theme }) => theme.primaryButtonHover};
        cursor: pointer;
      }
    }
  }
`;

const PresetFilterWrapper = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_md")};
  position: relative;
`;

type FilterFlyBarProps = {
  resetOffset?: React.Dispatch<React.SetStateAction<number>>;
  customLabels: TagClassificationConfig[];
  handleShowFilters: (show: boolean) => void;
  show: boolean;
  selectedPreset: IPresetDataItem | undefined;
  presetFilterData: IPresetFilter | undefined;
  mutatePresetFilter: () => void;
  handleActivePresetId: (
    id: string,
    isPresetSelected: boolean
  ) => Promise<void>;
  clearFilter: (preventPresetActivation: boolean) => void;
  /** Base filter option metadata (all options, no selection context). When
   *  provided, enables wildcard conversion for large filter selections and
   *  defers the sidebar request until metadata has loaded. Pass the same
   *  `filterMetadata` used by the parent's product list requests. */
  availableFilters?: Filters;
  entityType?: EntityType;
};

export function FiltersFlyBar({
  resetOffset = noop,
  customLabels,
  handleShowFilters,
  show,
  selectedPreset,
  presetFilterData,
  mutatePresetFilter,
  handleActivePresetId,
  clearFilter,
  availableFilters,
  entityType,
}: FilterFlyBarProps) {
  // "ArrayParam" is how useQueryParams handles things like
  // ?market_segment=abc&market_segment=xyz
  // That URL will become ["abc", "xyz"]

  const [filtersFromUrl, setFiltersFromUrl] =
    useState<{ [key: string]: string[] }>();

  const location = useLocation();
  const history = useHistory();
  const { roleIsGuest, roleIsSomeKindOfSeller, hasPermission } =
    useAuthContext();
  const canModify = entityType
    ? hasPermission(ENTITY_MODIFY_PERMISSIONS[entityType])
    : hasPermission("modify_products");

  const { notifyError, notifySuccess } = useContext(Notifications);
  const [pageFilters, setPageFilters] = useState<Filters>();
  const [searchResults, setSearchResults] = useState<IsearchFilterItem[]>();
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showPresetFilter, setShowPresetFilter] = useState(false);
  const [isUpdatePreset, setIsUpdatePreset] = useState(false);
  const [showPresetDialog, setShowPresetDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [appliedPresetIds, setAppliedPresetIds] = useState<Set<string>>(
    new Set()
  );

  const { storefront_id, slug: tenantSlug, tenant_id } = useStoreState();

  const [filtersSearchQuery, setFiltersSearchQuery] = useState("");

  const queryObject = () => {
    const querylist: any = {
      active_tag_view: StringParam,
      q: StringParam,
    };
    if (pageFilters) {
      for (const [key] of Object.entries(pageFilters)) {
        querylist[key] = ArrayParam;
      }
    }
    return querylist;
  };

  const [query] = useQueryParams(queryObject());
  const [activeTagView, setActiveTagView] = useState<string | null>(
    query.active_tag_view ?? null
  );

  const { t } = useTranslation();
  const { getTemplateDisplayName } = useMdmEntityDisplayNames();

  const [cookies] = useCookies([`preferred-language-${tenantSlug}`]);

  const preferredLanguage: SupportedLanguage | undefined = cookies[
    `preferred-language-${tenantSlug}`
  ] as SupportedLanguage;

  useEffect(() => {
    const filters = loadFilters(location);
    setFiltersFromUrl(filters);

    const filteredKeys = Object.keys(filters).filter(
      (key) =>
        key !== "perPage" && key !== "offset" && key !== "active_tag_view"
    );
    const hasValidFilters = filteredKeys.some(
      (key) => Array.isArray(filters[key]) && filters[key].length > 0
    );
    setShowPresetFilter(hasValidFilters);
    if (!filters.active_tag_view) {
      setActiveTagView(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname]);

  const { data, error: filtersError } =
    useAlternateRequest<ProductFiltersMainSchema>({
      filters: {
        ...filtersFromUrl,
        ...{ show_inactive: ["true"] },
        ...(!!activeTagView ? { active_tag_view: [activeTagView] } : {}),
      },
      url: entityType
        ? undefined
        : endpoints.v2_storefronts_id_products_filters(storefront_id),
      filterLabels: customLabels?.map((label) => label.filter_type) || [],
      availableFilters,
    });

  const { data: entityFiltersData } = useSWR<ProductFiltersMainSchema, Error>(
    entityType
      ? makeUrlWithParams(
          endpoints.v2_storefronts_id_mdm_entity_type_filters(
            storefront_id,
            entityType
          ),
          {
            // AP-1016 (Advanced Filters, Part 1): expand any numeric filter's
            // encoded criterion into its compiled `_op`/`_value`/`_min`/
            // `_max` params (docs/plans/NUMERIC_FILTERS_FE_SPEC.md §5.2) —
            // this fetch is a plain GET, same contract as EntityListPage.tsx's
            // own listing request.
            ...expandNumericFilterParams(filtersFromUrl ?? {}),
            ...{ show_inactive: ["true"] },
            ...(!!activeTagView ? { active_tag_view: [activeTagView] } : {}),
          }
        )
      : null
  );
  const entityFilters = entityFiltersData?.filters;

  const filters = entityType ? entityFilters : data?.filters;

  // AP-1016 (Advanced Filters, Part 1) — numeric filters live in a separate
  // sibling map, never inside `filters` (docs/plans/NUMERIC_FILTERS_FE_SPEC.md §3).
  const numericFilters = entityType
    ? entityFiltersData?.numeric_filters
    : data?.numeric_filters;

  // Status filters for seller_admin
  const { data: statusData } = useSWR<ProductStatusesSchema, Error>(
    roleIsSomeKindOfSeller && tenant_id
      ? endpoints.v2_tenants_tenant_id_pim_products_status(tenant_id)
      : null
  );
  const statuses = statusData?.statuses;

  // Template filters for seller_admin with view_templates permission
  const { data: templateData } = useSWR<
    WithPagination<{ data: AttributeTemplateSummarySchema[] }>,
    Error
  >(
    roleIsSomeKindOfSeller && tenant_id
      ? `${endpoints.v2_storefronts_id_pim_templates_summary(
          tenant_id
        )}?minimum_number_of_products=1&limit=100`
      : null
  );

  const handleFilter = ({
    values,
    filter,
  }: {
    values?: ChipType[];
    filter: string;
  }) => {
    // Load current filters
    const currentFilters = loadFilters(location);

    if (values) {
      const filtersArray = chipsToStringArray(values);
      const processedFiltersArray =
        filtersArray.length > 0 && filter === "status"
          ? filtersArray.map((filterValue) =>
              getProductStatusValue(filterValue, t)
            )
          : filtersArray;

      // Update filters with new values
      const updatedFilters: { [key: string]: string[] } = {
        ...currentFilters,
        active_tag_view: [filter],
      };

      if (processedFiltersArray.length > 0) {
        updatedFilters[filter] = processedFiltersArray;
      } else {
        delete updatedFilters[filter];
      }

      // Remove undefined/null values
      Object.keys(updatedFilters).forEach((key) => {
        if (!updatedFilters[key] || updatedFilters[key].length === 0) {
          delete updatedFilters[key];
        }
      });

      // Save using new utility
      const usedSessionStorage = saveFilters(
        updatedFilters,
        history,
        location.pathname,
        {
          q: query?.q || undefined,
        }
      );

      // Only notify if sessionStorage was actually used (true) and there are filters (not null)
      if (usedSessionStorage === true) {
        notifySuccess(
          t(
            "Filter set stored in browser session (too large for URL)"
          ) as string
        );
      }
    } else {
      // Remove the filter
      const updatedFilters: { [key: string]: string[] } = { ...currentFilters };
      delete updatedFilters[filter];
      if (!query.q) {
        delete updatedFilters.q;
      }

      // Save using new utility
      saveFilters(updatedFilters, history, location.pathname, {
        q: query?.q || undefined,
      });
    }
    resetOffset(0);
  };

  const handleChange = (val: React.ChangeEvent<HTMLInputElement>) => {
    if (val.target.value) {
      setShowSearchResults(true);
    }
    setFiltersSearchQuery(val.target.value);
  };

  const handleClearInput = () => setFiltersSearchQuery("");

  const getFilterName = (filter: IsearchFilterItem) => {
    const displayName =
      filter.type === "template"
        ? getTemplateDisplayName({
            template_name: filter.label,
            template_type: entityType,
          })
        : filter.label;
    if (
      filtersSearchQuery.length > 0 &&
      displayName.toLowerCase().indexOf(filtersSearchQuery.toLowerCase()) > -1
    ) {
      const start = displayName
        .toLowerCase()
        .indexOf(filtersSearchQuery.toLowerCase());
      const end = start + filtersSearchQuery.length;
      return (
        <>
          {displayName.substring(0, start)}
          <b>{displayName.substring(start, end)}</b>
          {displayName.substring(end, displayName.length)}
        </>
      );
    }
    return displayName;
  };

  const handleFilterClick = (filter: IsearchFilterItem) => {
    // Id-keyed facets (e.g. `user`) are applied by id; everything else by
    // the label (name).
    const isIdKeyedFacet =
      isIdKeyedFacetType(filter.valueType) && filter.id != null;
    const filterValue = isIdKeyedFacet
      ? String(filter.id)
      : filter.type === "status"
      ? getProductStatusValue(filter.label, t)
      : filter.label;

    // Check if filter already exists
    if (filtersFromUrl && filtersFromUrl[filter.type]?.includes(filterValue)) {
      return;
    }

    // Load current filters
    const currentFilters = loadFilters(location);

    // Add new filter to existing ones
    const updatedFilters: { [key: string]: string[] } = {
      ...currentFilters,
      [filter.type]: currentFilters[filter.type]
        ? [...currentFilters[filter.type], filterValue]
        : [filterValue],
    };

    // Save using new utility
    const usedSessionStorage = saveFilters(
      updatedFilters,
      history,
      location.pathname,
      {
        q: query?.q || undefined,
      }
    );

    // Only notify if sessionStorage was actually used (true) and there are filters (not null)
    if (usedSessionStorage === true) {
      notifySuccess(
        t("Filter set stored in browser session (too large for URL)") as string
      );
    }

    resetOffset(0);
  };

  const wrapperRef = useRef(document.createElement("div"));

  useEffect(() => {
    const handleClickOutsideSearchResult = (event: any) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        showSearchResults
      ) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("click", handleClickOutsideSearchResult, false);
    return () => {
      document.removeEventListener(
        "click",
        handleClickOutsideSearchResult,
        false
      );
    };
  }, [showSearchResults]);

  useEffect(() => {
    if (filters) {
      const filters_by_custom_labels: Filters = {};

      // First, process all custom labels (including status and template if they come from API)
      customLabels.forEach(({ filter_type }) => {
        if (filter_type === "status" && !canModify) {
          return;
        }
        if (filters[filter_type] && filters[filter_type].length > 0) {
          filters_by_custom_labels[filter_type] = filters[filter_type];
        }
      });

      // Add status filters from API if available, otherwise create from status data for seller_admin
      if (canModify && filters.status && filters.status.length > 0) {
        // Use status filters from API but ensure they show translated text
        filters_by_custom_labels.status = filters.status.map((item) => ({
          ...item,
          name: getProductStatusText(item.name as ProductStatusType, t),
        }));
      } else if (roleIsSomeKindOfSeller && statuses && canModify) {
        // Fallback: create status filters from status data (counts will be 0)
        filters_by_custom_labels.status = (statuses as string[]).map(
          (item) => ({
            name: getProductStatusText(item as ProductStatusType, t),
            count: 0,
            image_url: null,
            selected: false,
          })
        );
      }

      // Add template filters from API if available, otherwise create from template data for seller_admin
      if (filters.template && filters.template.length > 0) {
        // Use template filters from API (they have counts)
        filters_by_custom_labels.template = filters.template;
      } else if (roleIsSomeKindOfSeller && templateData) {
        // Fallback: create template filters from template data (counts will be 0)
        filters_by_custom_labels.template = templateData.data
          // .filter((item) =>
          //   entityType
          //     ? item.template_type === entityType
          //     : item.template_type === "product" || item.template_type === null
          // )
          .map((item) => ({
            id: item.template_id,
            name: item.template_name,
            count: 0,
            image_url: null,
            selected: false,
          }));
      }

      setPageFilters(filters_by_custom_labels);
    }
  }, [
    customLabels,
    filters,
    statuses,
    templateData,
    t,
    roleIsSomeKindOfSeller,
    canModify,
    entityType,
  ]);

  useEffect(() => {
    let filtersList: IsearchFilterItem[] = [];
    if (pageFilters) {
      for (const [key, value] of Object.entries(pageFilters)) {
        value.forEach((item) => {
          const alreadySelected = (query[key] ?? []).some(
            (queryValue: unknown) => facetOptionValueMatches(item, queryValue)
          );
          if (!alreadySelected) {
            filtersList.push({
              type: key,
              label: item.name,
              id: item.id,
              valueType: item.type,
            });
          }
        });
      }
      setSearchResults(
        filtersList.filter((item) => {
          const displayName =
            item.type === "template"
              ? getTemplateDisplayName({
                  template_name: item.label,
                  template_type: entityType,
                })
              : item.label;
          return (
            displayName
              .toLocaleLowerCase()
              .indexOf(filtersSearchQuery.toLocaleLowerCase()) > -1
          );
        })
      );
    }
  }, [
    pageFilters,
    filtersSearchQuery,
    query,
    getTemplateDisplayName,
    entityType,
  ]);

  const handleSetFiltersInUrl = React.useCallback(
    (filters: IFilters) => {
      // Convert IFilters to Filters format
      const convertedFilters = convertToFiltersFormat(filters, t);

      // Use saveFilters to handle storage decision automatically
      const usedSessionStorage = saveFilters(
        convertedFilters,
        history,
        location.pathname,
        {
          q: query?.q || undefined,
        }
      );

      // Only notify if sessionStorage was actually used (true) and there are filters (not null)
      if (usedSessionStorage === true) {
        notifySuccess(
          t(
            "Filter set stored in browser session (too large for URL)"
          ) as string
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, location.pathname, query, t, notifySuccess]
  );

  // Computed synchronously (not via a useEffect+setState round trip) so a
  // freshly-mounted "user"-type filter chip never has a render where its raw
  // id is shown before the matching display name is resolved.
  const filteredItems: IFilters = useMemo(() => {
    if (!pageFilters || !filtersFromUrl) return {};

    const { offset, perPage, active_tag_view, ...validFilters } =
      filtersFromUrl;
    const results: IFilters = {};

    for (const key in validFilters) {
      const filterValues = validFilters[key];
      if (!Array.isArray(filterValues)) continue;

      // AP-1016 (Advanced Filters, Part 1) — a numeric filter's applied
      // value is a single encoded operator+value(s) string (see
      // `encodeNumericFilterCriterion`), not a facet selection, and its key
      // never appears in `pageFilters` (see `numericFilters`'s doc comment
      // above). Round-trip the raw encoded string through `IFilters` as-is
      // so presets can save/restore/compare it the same way `filterutils.ts`
      // already does for URL/request params.
      if (filterValues.length === 1) {
        const criterion = decodeNumericFilterCriterion(filterValues[0]);
        if (criterion) {
          results[key] = [{ id: null, name: filterValues[0], type: "numeric" }];
          continue;
        }
      }

      const mainList = pageFilters[key] || [];
      if (!Array.isArray(mainList)) continue;

      if (key === "status") {
        // Special handling for status filters
        const matched = mainList
          .filter((item) => {
            return filterValues.some((filterValue: string) => {
              const productStatusText = getProductStatusText(
                filterValue as ProductStatusType,
                t
              );
              return item.name === productStatusText;
            });
          })
          .map((item) => ({
            id: item.id ? String(item.id) : null,
            name: item.name, // Keep the translated display name
            type: item.type,
          }));
        results[key] = matched;
      } else {
        const matched = mainList
          .filter((item) =>
            // `user` facets store the id in the URL; match it back to the entry
            // so the chip still displays the name.
            filterValues.some((filterValue) =>
              facetOptionValueMatches(item, filterValue)
            )
          )
          .map((item) => ({
            id: item.id ? String(item.id) : null,
            name: item.name,
            type: item.type,
          }));
        results[key] = matched;
      }
    }

    return results;
  }, [pageFilters, filtersFromUrl, t]);

  const areArraysEqual = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) => value === sortedB[index]);
  };

  // Compare against the currently *selected* preset, not the first entry in
  // the list — `presetFilterData.data` is not sorted selected-first, so
  // indexing [0] compared the current filters against an arbitrary preset
  // (e.g. showing "Update Preset" right after creating a new one, if that
  // new preset didn't happen to land at index 0).
  //
  // Compared against `filtersFromUrl` (per filter key), not the
  // facet-derived `filteredItems` — `filteredItems` only resolves once
  // `pageFilters` has round-tripped to the filter-metadata endpoint, which
  // lags a beat behind the URL. Applying a preset updates `selectedPreset`
  // (via the preset-select PATCH) and the URL independently, so comparing
  // against `filteredItems` caught that lag as a mismatch and showed
  // "Update Preset" immediately after applying a preset that was never
  // touched. `filtersFromUrl` mirrors the exact values `handleSetFiltersInUrl`
  // wrote via `convertToFiltersFormat`, so comparing the preset's filters
  // through the same conversion is an apples-to-apples check that updates
  // as soon as the URL does, no facet metadata required.
  useEffect(() => {
    const filter = selectedPreset?.filters;

    if (!filter) {
      setIsUpdatePreset(true);
      return;
    }

    if (!filtersFromUrl) return;

    const presetFilters = convertToFiltersFormat(filter, t);
    const { offset, perPage, active_tag_view, q, ...appliedFilters } =
      filtersFromUrl;

    const keys = new Set([
      ...Object.keys(presetFilters),
      ...Object.keys(appliedFilters),
    ]);
    const result = Array.from(keys).every((key) =>
      areArraysEqual(
        (presetFilters[key] ?? []).map((value) => value.toLocaleLowerCase()),
        (appliedFilters[key] ?? []).map((value) => value.toLocaleLowerCase())
      )
    );
    setIsUpdatePreset(result);
  }, [filtersFromUrl, selectedPreset, t]);

  // Auto-apply the selected preset's filters once per mount, mirroring
  // `useFilterPresets`'s own guarded auto-apply — this covers callers (e.g.
  // the public portfolio view) that don't pass location/history/pathname
  // into that hook and so skip its effect. Gated by `appliedPresetIds` so it
  // never re-fires once applied: without that guard, this previously kept
  // re-checking `data[0]` (the wrong preset when it isn't selected) and
  // stomped the URL back to the preset's filters any time the user cleared
  // their selections down to zero, making "clear/uncheck a filter" appear to
  // do nothing.
  useEffect(() => {
    const selectedPresetData = presetFilterData?.data?.find(
      (item) => item.selected
    );

    if (
      !selectedPresetData?.filters ||
      appliedPresetIds.has(selectedPresetData.id)
    ) {
      return;
    }
    if (getStorageMethod(location) !== null) return;

    setAppliedPresetIds((prev) => new Set(prev).add(selectedPresetData.id));
    handleSetFiltersInUrl(selectedPresetData.filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetFilterData, appliedPresetIds, location, handleSetFiltersInUrl]);

  const shouldShowPresetFilter =
    (showPresetFilter || (presetFilterData?.data?.length ?? 0) > 0) &&
    !roleIsGuest;

  const handleSubmitPreset = async (preset_name: string) => {
    setShowPresetDialog(false);
    const presetId = selectedPreset?.id || "";
    try {
      const request = isEditMode
        ? axios.patch(
            entityType
              ? endpoints.v2_storefronts_id_user_filters_type_userFilterId(
                  storefront_id,
                  entityType as string,
                  presetId
                )
              : endpoints.v2_storefronts_id_user_filters_userFilterId(
                  storefront_id,
                  presetId
                ),
            { name: preset_name }
          )
        : axios.post(
            entityType
              ? endpoints.v2_storefronts_id_user_filters_type(
                  storefront_id,
                  entityType as string
                )
              : endpoints.v2_storefronts_id_user_filters(storefront_id),
            {
              name: preset_name,
              filters: filteredItems,
              selected: true,
            }
          );

      const response = await request;

      if (response.status === 201) {
        setIsEditMode(false);
        mutatePresetFilter();
        notifySuccess(
          t(isEditMode ? "Preset updated" : "New Preset saved") as string
        );
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    }
  };

  const handleDeletePreset = async (id: string) => {
    try {
      const response = await axios.delete(
        entityType
          ? endpoints.v2_storefronts_id_user_filters_type_userFilterId(
              storefront_id,
              entityType,
              id
            )
          : endpoints.v2_storefronts_id_user_filters_userFilterId(
              storefront_id,
              id
            )
      );
      if (response.status === 204) {
        mutatePresetFilter();
        notifySuccess(t("Preset deleted") as string);
        clearFilter(true);
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    }
  };

  const handleUpdatePreset = async () => {
    const presetId = selectedPreset?.id || "";
    try {
      const response = await axios.patch(
        entityType
          ? endpoints.v2_storefronts_id_user_filters_type_userFilterId(
              storefront_id,
              entityType,
              presetId
            )
          : endpoints.v2_storefronts_id_user_filters_userFilterId(
              storefront_id,
              presetId
            ),
        {
          filters: filteredItems,
        }
      );
      if (response.status === 201) {
        mutatePresetFilter();
        notifySuccess(t("Preset updated") as string);
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    }
  };

  const handleApplyPreset = async (
    id: string,
    setShowDropdown: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    // Look up the clicked preset by id — every row in the dropdown invokes
    // this with its own id, so applying always picked the first preset's
    // filters regardless of which one was clicked.
    const filters = presetFilterData?.data?.find(
      (item) => item.id === id
    )?.filters;
    if (filters) {
      // Apply to the URL first: `handleActivePresetId` awaits a PATCH +
      // SWR revalidation before `selectedPreset` updates, so doing it first
      // left a render where `selectedPreset` already pointed at this preset
      // but `filtersFromUrl` still held the old values — a spurious
      // mismatch that flashed "Update Preset" right after applying one.
      handleSetFiltersInUrl(filters);
      setShowDropdown(false);
      await handleActivePresetId(id, true);
    }
  };

  // Could potentially display an error notification after multiple failed
  // attempts. That is not yet set up in this project.
  if (filtersError) {
    // Search still works if filters don't load.
    // This maintains the layout of the page in case of error.
    // On mobile, show the close button so users can exit the filters view.
    return (
      <FiltersFlyBarWrapper show={show}>
        {show && (
          <FiltersHeaderWrapper>
            <FiltersHeader>
              <span>{t("Filters")}</span>
              <InvisibleButton onClick={() => handleShowFilters(false)}>
                <XIcon width={16} height={16} />
              </InvisibleButton>
            </FiltersHeader>
          </FiltersHeaderWrapper>
        )}
        <EmptyFiltersPlaceholder />
      </FiltersFlyBarWrapper>
    );
  } else {
    // Handle case that happened once on staging where all filters were empty arrays.
    // This might theoretically be possible if a tenant is activated and no filters
    // are added.

    return (
      <FiltersFlyBarWrapper show={show}>
        {show && (
          <>
            <FiltersHeaderWrapper>
              <FiltersHeader>
                <span>{t("Filters")}</span>
                <InvisibleButton onClick={() => handleShowFilters(false)}>
                  <XIcon width={16} height={16} />
                </InvisibleButton>
              </FiltersHeader>
              <SearchBarWrapper ref={wrapperRef}>
                <SearchBar
                  placeHolder={t("Search for filters")}
                  query={filtersSearchQuery}
                  handleChange={handleChange}
                  handleClearInput={handleClearInput}
                  onFocus={() => {
                    if (searchResults?.length && filtersSearchQuery.length) {
                      setShowSearchResults(true);
                    }
                  }}
                />
                {showSearchResults && filtersSearchQuery.length > 0 && (
                  <SearchResultsWrapper>
                    <ul>
                      {searchResults?.map((result) => (
                        <li
                          onClick={() => {
                            handleFilterClick(result);
                          }}
                        >
                          {getCustomLabel({
                            filter_type: result.type,
                            tag_classification_configs: customLabels,
                            preferred_language: preferredLanguage,
                          })}{" "}
                          {` > `}
                          {getFilterName(result)}
                        </li>
                      ))}
                    </ul>
                  </SearchResultsWrapper>
                )}
              </SearchBarWrapper>
              {shouldShowPresetFilter && (
                <PresetFilterWrapper>
                  <PresetFilter
                    dropdownWidth="100%"
                    presetFilterData={presetFilterData}
                    isUpdatePreset={isUpdatePreset}
                    selectedPreset={selectedPreset}
                    showPresetDialog={showPresetDialog}
                    setShowPresetDialog={setShowPresetDialog}
                    setIsEditMode={setIsEditMode}
                    onSubmitPreset={handleSubmitPreset}
                    onDeletePreset={handleDeletePreset}
                    onApplyPreset={handleApplyPreset}
                    onUpdatePreset={handleUpdatePreset}
                  />
                </PresetFilterWrapper>
              )}
            </FiltersHeaderWrapper>

            <FiltersWrapper>
              {pageFilters &&
                Object.entries(pageFilters).map((FilterItem, index) => {
                  return (
                    <FilterGroup
                      key={FilterItem[0]}
                      setter={handleFilter}
                      options={FilterItem[1]}
                      filters={pageFilters}
                      customLabels={customLabels}
                      entityType={entityType}
                      expanded={
                        (index === 0 && !activeTagView) ||
                        activeTagView === FilterItem[0]
                      }
                      setActiveTagView={setActiveTagView}
                      filterType={FilterItem[0]}
                      preselectedFilters={
                        FilterItem[0] === "status" &&
                        filteredItems[FilterItem[0]]
                          ? filteredItems[FilterItem[0]].map((item) => ({
                              name: item.name,
                            }))
                          : // Id-keyed facets (e.g. `user`) store the id in the
                          // URL; seed from the id-aware reconciled items so the
                          // chip shows the name while the applied value stays
                          // the id.
                          (FilterItem[1] ?? []).some((option) =>
                              isIdKeyedFacetType(option.type)
                            ) && filteredItems[FilterItem[0]]
                          ? filteredItems[FilterItem[0]].map((item) => ({
                              name: item.name,
                              id: item.id ?? undefined,
                              type: item.type,
                            }))
                          : convertToChipArray(
                              filtersFromUrl
                                ? filtersFromUrl[FilterItem[0]]
                                : []
                            ) ?? []
                      }
                    />
                  );
                })}
              {/* AP-1016 (Advanced Filters, Part 1) — rendered from the
                  separate `numeric_filters` map, never from `pageFilters`. */}
              {numericFilters &&
                Object.entries(numericFilters).map(([filterType, meta]) => (
                  <FilterGroup
                    key={filterType}
                    setter={handleFilter}
                    numericOperators={meta.operators}
                    customLabels={customLabels}
                    expanded={activeTagView === filterType}
                    setActiveTagView={setActiveTagView}
                    filterType={filterType}
                    preselectedFilters={
                      convertToChipArray(
                        filtersFromUrl ? filtersFromUrl[filterType] : []
                      ) ?? []
                    }
                  />
                ))}
            </FiltersWrapper>
          </>
        )}
      </FiltersFlyBarWrapper>
    );
  }
}
